import { Checkbox, Chip, Separator } from '@zep-us/zus';
import { CheckBoldIcon } from '@zep-us/zus-icons';
import { useTranslation } from 'next-i18next';
import { QuizCard } from '@/shared/components/QuizCardV2';
import { cn } from '@/utils/tailwind.util';
import { type QuizBankProblem, type QuizBankStandard } from '../types';
import { QuizBankProblemCardFooter } from './QuizBankProblemCardFooter';
interface QuizBankProblemCardProps {
  problem: QuizBankProblem;
  selected: boolean;
  /** 이미 빌드에 담긴 문제 — 다시 담을 수 없다 */
  added?: boolean;
  /** 선택 상한에 걸려 더 고를 수 없다 (이미 고른 카드는 해제 가능) */
  selectionDisabled?: boolean;
  onToggle: (problem: QuizBankProblem) => void;
  onStandardClick?: (standard: QuizBankStandard) => void;
}

/** `QuizCardV2` 아톰에 선택 체크박스와 출처 푸터만 얹은 카드. */
export const QuizBankProblemCard = ({
  problem,
  selected,
  added = false,
  selectionDisabled = false,
  onToggle,
  onStandardClick
}: QuizBankProblemCardProps) => {
  const {
    t
  } = useTranslation(['build']);
  const locked = added || selectionDisabled;
  const handleToggle = () => {
    if (locked) return;
    onToggle(problem);
  };
  return <QuizCard.Root className={cn('relative shadow-xs transition-shadow', added && 'opacity-60', locked ? 'cursor-not-allowed' : 'cursor-pointer hover:shadow-sm',
  // 선택 테두리는 오버레이로 그린다 — ring-inset 은 배경 있는 푸터에 가려 아래쪽이 끊긴다.
  selected && 'after:pointer-events-none after:absolute after:inset-0 after:rounded-[inherit] after:ring-2 after:ring-inset after:ring-strong-default')} onClick={handleToggle} data-sentry-element="unknown" data-sentry-component="QuizBankProblemCard" data-sentry-source-file="QuizBankProblemCard.tsx">
      {/* 상세 페이지(`QuizItemCard`)와 같은 세로 리듬 — Header 는 상단 여백만 만든다 */}
      <QuizCard.Header data-sentry-element="unknown" data-sentry-source-file="QuizBankProblemCard.tsx" />

      <QuizCard.Content data-sentry-element="unknown" data-sentry-source-file="QuizBankProblemCard.tsx">
        <div className="flex items-center justify-between gap-sm">
          <div className="flex min-w-0 items-center gap-sm">
            <QuizCard.TypeBadge type={problem.type} data-sentry-element="unknown" data-sentry-source-file="QuizBankProblemCard.tsx" />
            {added && <Chip size="sm" className="shrink-0">
                {t('build:quizBank.card.added')}
              </Chip>}
          </div>

          <div className="flex shrink-0 items-center gap-sm">
            {/* 정답률 없음은 0% 와 다른 상태라 칸을 통째로 비운다 */}
            {problem.answerRate !== undefined && <>
                <Chip size="md" rounded="full" className="shrink-0 bg-neutral tabular-nums" leftSlot={<CheckBoldIcon size="xs" />}>
                  {t('build:quizBank.card.answerRate', {
                rate: Math.round(problem.answerRate * 100)
              })}
                </Chip>
                <Separator orientation="vertical" className="h-[12px] self-center bg-gray-300" />
              </>}

            <Checkbox checked={selected || added} disabled={locked} aria-label={t('build:quizBank.card.select')} onClick={event => event.stopPropagation()} onCheckedChange={handleToggle} data-sentry-element="Checkbox" data-sentry-source-file="QuizBankProblemCard.tsx" />
          </div>
        </div>

        <QuizCard.QuestionText text={problem.stem} data-sentry-element="unknown" data-sentry-source-file="QuizBankProblemCard.tsx" />

        {problem.imageUrl && <QuizCard.Image.QuestionImage src={problem.imageUrl} alt="" />}

        {problem.type === 'multiple' && problem.options && <QuizCard.MultipleChoice.Container>
            {problem.options.map((option, index) => <QuizCard.MultipleChoice.Item key={index} text={option.richText || option.text} imageUrl={option.imageUrl} isAnswer={problem.answerIndexes?.includes(index) ?? false} choiceNumber={index}
        // 오답은 흐리게 (Figma)
        textClassName={cn('text-heading-sm-bold', !problem.answerIndexes?.includes(index) && 'text-assistive')} />)}
          </QuizCard.MultipleChoice.Container>}

        {problem.type === 'ox' && problem.oxAnswer && <QuizCard.OXChoice.Container answer={problem.oxAnswer} />}

        {problem.type === 'subjective' && problem.answerTexts && <QuizCard.SubjectiveAnswer.Container>
            {problem.answerTexts.map((answer, index) => <QuizCard.SubjectiveAnswer.Item key={index} text={answer} />)}
          </QuizCard.SubjectiveAnswer.Container>}
      </QuizCard.Content>

      <QuizCard.Footer className="bg-neutral px-lg pb-lg pt-md" data-sentry-element="unknown" data-sentry-source-file="QuizBankProblemCard.tsx">
        <QuizBankProblemCardFooter standard={problem.standard} author={problem.author} onStandardClick={onStandardClick} data-sentry-element="QuizBankProblemCardFooter" data-sentry-source-file="QuizBankProblemCard.tsx" />
      </QuizCard.Footer>
    </QuizCard.Root>;
};